import isEmpty from 'lodash/isEmpty';
import React, {Component} from 'react';
import PropTypes from 'prop-types';
import PureRenderMixin from 'react-addons-pure-render-mixin';
import classNames from 'classnames';
import moment from 'moment';
import uuidV4 from 'uuid/v4';
import TetherComponent from 'react-tether';
import DOMUtils from '@spothero/utils/dom';
import DateUtils from 'date-utils';
import SHGlobals from 'sh-globals';
import DatePickerCalendar from 'date-picker-calendar-component';
import Label from 'label-component';
import Textfield from 'textfield-component';
import RenderInBody from 'render-in-body-component';

class DatePicker extends Component {
    constructor(props) {
        super(props);

        const {
            selectedDate
        } = props;

        this._IS_NATIVE_DATE_INPUT = SHGlobals.isMobile && DateUtils.supportsDateInput();
        this._DATE_FORMAT = (this._IS_NATIVE_DATE_INPUT) ? SHGlobals.MOBILE_DATE_FORMAT : SHGlobals.DESKTOP_DATE_FORMAT;
        this._DATE_PICKER_ID = uuidV4();
        this._TETHER_CLASS = `DatePicker-tether-${this._DATE_PICKER_ID}`;
        this._onBodyClick = this._onBodyClick.bind(this);
        this._onInputClick = this._onInputClick.bind(this);
        this._onInputChange = this._onInputChange.bind(this);
        this._onDateChange = this._onDateChange.bind(this);

        this.state = {
            showCalendar: false,
            selectedDate: (this._IS_NATIVE_DATE_INPUT && selectedDate) ? DateUtils.convertToMobile(selectedDate) : selectedDate
        };
        this.shouldComponentUpdate = PureRenderMixin.shouldComponentUpdate.bind(this);
    }

    componentWillReceiveProps(nextProps) {
        const {
            selectedDate
        } = nextProps;
        const nextSelectedDate = (this._IS_NATIVE_DATE_INPUT) ? DateUtils.convertToMobile(selectedDate) : selectedDate;

        if (!isEmpty(nextSelectedDate) && nextSelectedDate !== this.state.selectedDate) {
            this.setState({
                selectedDate: nextSelectedDate
            });
        }
    }

    componentDidUpdate(prevProps, prevState) {
        const {
            onChange,
            focus
        } = this.props;
        const {
            showCalendar,
            selectedDate
        } = this.state;

        if (!this._IS_NATIVE_DATE_INPUT
            && showCalendar
            && (showCalendar !== prevState.showCalendar)) {
            document.body.addEventListener('click', this._onBodyClick);
        } else {
            this._removeBodyClickListener();
        }

        if (selectedDate !== prevState.selectedDate && onChange) {
            onChange(selectedDate);
        }

        if (!selectedDate && focus) {
            this._input.focus();
        }
    }

    componentWillUnmount() {
        this._removeBodyClickListener();
    }

    _removeBodyClickListener() {
        if (this._IS_NATIVE_DATE_INPUT) { return; }

        document.body.removeEventListener('click', this._onBodyClick);
    }

    _onBodyClick(evt) {
        if (!DOMUtils.parent(evt.target, `.DatePicker-${this._DATE_PICKER_ID}`)
            && !DOMUtils.parent(evt.target, `.${this._TETHER_CLASS}`)) {
            this.setState({
                showCalendar: false
            });
        }
    }

    _onInputClick(evt) {
        this.setState({
            showCalendar: true
        });
    }

    _onInputChange(selectedDate) {
        this.setState({
            selectedDate
        });
    }

    _onDateChange(selectedDate) {
        // this is absolutely asinine but without the setTimeout in some situations (when used in conjunction with ReactDOM.render directly into a non-React page)
        // this was causing invariant issues. Wrapping it in the setTimeout worked and I'm fine with that cuz this is monolith - Matt P.
        setTimeout(() => {
            this.setState({
                showCalendar: false,
                selectedDate
            });
        }, 0);
    }

    _renderInput() {
        const {
            useIcon,
            placeholder,
            disableBefore,
            disableAfter,
            disablePast
        } = this.props;
        const {
            selectedDate
        } = this.state;
        const inputChange = (this._IS_NATIVE_DATE_INPUT) ? this._onInputChange : null;
        let min = null;
        let max = null;

        if (this._IS_NATIVE_DATE_INPUT) {
            const start = (disablePast) ? moment() : null;
            const before = (disableBefore) ? DateUtils.getAsMoment(disableBefore) : null;

            max = (disableAfter) ? DateUtils.getAsMoment(disableAfter).format(this._DATE_FORMAT) : null;

            if (start) {
                min = start.format(this._DATE_FORMAT);
            }

            if (before && before.isAfter(start)) {
                min = before.format(this._DATE_FORMAT);
            }
        }

        return (
            <Textfield
                ref={node => { this._input = node; }}
                type={(this._IS_NATIVE_DATE_INPUT) ? 'date' : 'text'}
                value={selectedDate}
                onChange={inputChange}
                onClick={this._onInputClick}
                onIconClick={this._onInputClick}
                min={min}
                max={max}
                icon={(useIcon) ? 'calendar' : null}
                placeholder={placeholder}
                readOnly={!this._IS_NATIVE_DATE_INPUT}
                size="lg"
            />
        );
    }

    _renderCalendar() {
        const {
            calendarClassName,
            disablePast,
            disableBefore,
            disableAfter
        } = this.props;

        return (
            <DatePickerCalendar
                className={calendarClassName}
                onChange={this._onDateChange}
                selectedDate={this.state.selectedDate}
                disablePast={disablePast}
                disableBefore={disableBefore}
                disableAfter={disableAfter}
            />
        );
    }

    _renderInputContainer() {
        const {
            label,
            useIcon,
            useTetherShim
        } = this.props;
        const {
            showCalendar
        } = this.state;
        const containerClasses = classNames(
            'DatePicker-input-container',
            {'DatePicker-input-container-with-icon': useIcon}
        );
        const tetherClasses = classNames(
            'DatePicker-tether',
            {'DatePicker-tether-centered-with-shim': useTetherShim
                || (SHGlobals.isMobile && !DateUtils.supportsDateInput())
            },
            this._TETHER_CLASS
        );
        const renderCalendar = (showCalendar && !this._IS_NATIVE_DATE_INPUT);

        return (
            <div className={containerClasses}>
                {label && <Label>{label}</Label>}
                <TetherComponent
                    className={tetherClasses}
                    attachment="top left"
                    targetAttachment="bottom left"
                >
                    {this._renderInput()}
                    {renderCalendar && this._renderCalendar()}
                </TetherComponent>
                {(renderCalendar && useTetherShim) &&
                    <RenderInBody className="DatePicker-calendar-shim">
                        <div className="shim" />
                    </RenderInBody>
                }
            </div>
        );
    }

    // A stop-gap for non-react views rendering this component to set the value in case of an update
    // This can be removed later when this component is only being rendered by React parents and not Backbone views
    setValue(value) {
        this.setState({
            selectedDate: value
        });
    }

    get node() {
        return this._input;
    }

    render() {
        const {
            className,
            label
        } = this.props;
        const classes = classNames(
            'DatePicker',
            `DatePicker-${this._DATE_PICKER_ID}`,
            {'DatePicker-no-label': !label},
            className
        );

        return (
            <div className={classes}>
                {this._renderInputContainer()}
            </div>
        );
    }
}

DatePicker.propTypes = {
    className: PropTypes.string,
    calendarClassName: PropTypes.string,
    /** A string in the format 'MM/DD/YYYY' representing the date which should be selected. */
    selectedDate: PropTypes.string,
    useIcon: PropTypes.bool,
    label: PropTypes.node,
    placeholder: PropTypes.string,
    onChange: PropTypes.func,
    disablePast: PropTypes.bool,
    focus: PropTypes.bool,
    disableBefore: PropTypes.string,
    disableAfter: PropTypes.string,
    /** Puts the calendar in the center of the page with a shim behind it. */
    useTetherShim: PropTypes.bool
};

DatePicker.defaultProps = {
    selectedDate: '',
    disablePast: true
};

export default DatePicker;



// WEBPACK FOOTER //
// ./frontend/src/js/components/date-picker/DatePicker.jsx